<template>
  <div id="sales-history" class="sales-history">
    <b-modal ref="salesHistoryModal" size="lg" no-close-on-backdrop hide-footer :static="true" title="Sales History"
      @hide="cancel">
      <b-form class="sales-history-range-form" @submit.prevent="build(false)">
        <b-row>
          <b-col col cols="12" xl="5">
            <b-input-group>
              <b-form-input id="sales-start-date" v-model="salesStartDateString" type="text" size="sm"
                placeholder="MM/DD/YY" autocomplete="off" maxlength="8" @input="onStartDateChange"></b-form-input>
              <b-input-group-append>
                <b-form-datepicker button-only left id="sales-start-date-icon" size="sm" :max="salesEndDate"
                  value-as-date hide-header :date-format-options="{
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  }" locale="en" v-model="salesStartDate" @context="onStartDateContext"></b-form-datepicker>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col col cols="12" xl="5">
            <b-input-group>
              <b-form-input id="sales-end-date" v-model="salesEndDateString" type="text" size="sm"
                placeholder="MM/DD/YY" autocomplete="off" maxlength="8" :state="endDateState"
                @input="onEndDateChange"></b-form-input>
              <b-input-group-append>
                <b-form-datepicker button-only left id="sales-end-date-icon" size="sm" :min="salesStartDate"
                  :max="maxEnd" value-as-date hide-header :date-format-options="{
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  }" locale="en" v-model="salesEndDate" @context="onEndDateContext"></b-form-datepicker>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col col cols="12" xl="2">
            <b-button id="sales-history-filter-btn" type="submit" variant="kag-dark-blue" block size="sm"
              :disabled="endDateState === false">Filter</b-button>
          </b-col>
        </b-row>
        <div class="py-2">Quick Actions:</div>
        <b-row>
          <b-col col cols="12" xl="5">
            <b-button id="quickCalendar7" ref="quickCalendar7"
              :variant="useSevenDays ? 'primary mr-3' : 'outline-primary mr-3'" @click="quickCalendar(7)">7
              Days</b-button>
            <b-button id="quickCalendar14" ref="quickCalendar14" :variant="useFourteenDays ? 'primary mr-3' : 'outline-primary mr-3'
              " @click="quickCalendar(14)">14 Days</b-button>
            <b-button id="quickCalendar21" ref="quickCalendar21" :variant="useTwentyoneDays ? 'primary mr-3' : 'outline-primary mr-3'
              " @click="quickCalendar(21)">21 Days</b-button>
          </b-col>
        </b-row>
      </b-form>
      <div :class="!dataGroupedByDate ? 'mb-3' : ''">
        <div v-for="(group, key) in dataGroupedByDate" :key="key">
          <h3 class="mt-3" :class="isToday(key)">
            {{ key }} {{ isToday(key) ? "TODAY" : "" }}
          </h3>
          <b-table id="sales-history-table" class="sales-history-table" bordered small no-border-collapse
            responsive="lg" primary-key="id" :items="group" :fields="tableFields" :sort-by="'salesTimeStamp'"
            :sort-desc="true">
            <template v-for="field in tableFields" v-slot:[`cell(${field.key})`]="{ item }">
              <template>
                <div v-if="
                  item[field.key] &&
                  field.key !== 'actions' &&
                  field.key !== 'salesAmount'
                " :key="field.key">
                  {{ item[field.key] }}
                </div>
                <div v-if="field.key == 'salesAmount'" :key="field.key">
                  <div v-if="!item.isEditing">{{ item[field.key] }}</div>
                  <div v-else>
                    <b-form-input v-model="item.salesAmount" type="number" :state="salesAmountState(item.salesAmount)" placeholder="Amount"></b-form-input>
                  </div>
                </div>
                <div class="td-content" v-if="field.key == 'actions'" :key="field.key">
                  <b-button v-if="!item.isEditing" variant="kag-light-blue" :id="'sales-history-edit-btn-' + item.id"
                    class="mr-2 edit-btn clickable" size="sm" @click="editItem(item)" :disabled="!canUpdateSales"
                    v-b-tooltip.hover="'Edit'">
                    <font-awesome-icon class="clickable" icon="pen-to-square" />
                  </b-button>
                  <b-button v-if="item.isEditing" :disabled="!salesAmountState(item.salesAmount)" variant="success" :id="'sales-history-edit-confirm-btn-' + item.id"
                    class="mr-2 edit-btn clickable" size="sm" @click="editItemConfirm(item)"
                    v-b-tooltip.hover="'Confirm'">
                    <font-awesome-icon class="clickable" icon="fa-solid fa-check" />
                  </b-button>
                  <b-button v-if="item.isEditing" variant="danger" :id="'sales-history-edit-cancel-btn-' + item.id"
                    class="mr-2 edit-btn clickable" size="sm" @click="editItemCancel(item)"
                    v-b-tooltip.hover="'Cancel'">
                    <font-awesome-icon class="clickable" icon="fa-solid fa-ban" />
                  </b-button>
                </div>
              </template>
            </template>
          </b-table>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { RepositoryFactory } from '@/repositories/RepositoryFactory'
import moment from 'moment-timezone'
import global from '@/mixins/shared/global'
import userAuthGetters from '@/mixins/shared/userAuthGetters'

const SalesRepo = RepositoryFactory.get('SalesRepository')

export default {
  name: 'SalesHistory',
  mixins: [userAuthGetters],
  data () {
    const startDate = new Date()
    startDate.setHours(0)
    startDate.setMinutes(0)
    startDate.setSeconds(0)
    startDate.setMilliseconds(0)
    startDate.setDate(startDate.getDate() - 7)
    const endDate = new Date()
    endDate.setHours(23)
    endDate.setMinutes(59)
    endDate.setSeconds(59)
    endDate.setMilliseconds(999)
    const maxEndDate = new Date()
    maxEndDate.setHours(23)
    maxEndDate.setMinutes(59)
    maxEndDate.setSeconds(59)
    maxEndDate.setMilliseconds(999)

    return {
      siteId: null,
      sales: [],
      tableFields: [
        { key: 'commodityClass', label: 'Commodity Class' },
        { key: 'commodityCode', label: 'Commodity Code' },
        { key: 'salesAmount', label: 'Amount' },
        { key: 'actions', label: 'Actions' }
      ],
      salesStartDateString: global.buildShortDateString(startDate),
      salesStartDate: startDate,
      salesEndDateString: global.buildShortDateString(endDate),
      salesEndDate: endDate,
      maxEnd: maxEndDate,
      useSevenDays: true,
      useFourteenDays: false,
      useTwentyoneDays: false
    }
  },
  methods: {
    build (usingQuickCalendar, firstBuild = false) {
      if (this.siteId == null) return
      if (!usingQuickCalendar) {
        this.useSevenDays = false
        this.useFourteenDays = false
        this.useTwentyoneDays = false
      }
      if (firstBuild) {
        this.useSevenDays = true
      }
      this.sales = []
      SalesRepo.getSalesHistoryByDate(
        this.siteId,
        this.salesStartDate,
        this.salesEndDate
      ).then((response) => {
        if (response && response.data && response.data.length) {
          this.sales = response.data
          this.sales.forEach((s) => {
            s.salesTimeStamp = this.formatDate(new Date(s.salesTimeStamp))
          })
        }
      })
    },
    cancel () {
      this.clear()
      this.$refs.salesHistoryModal.hide()
    },
    show (opts = {}) {
      if (opts.siteId) {
        this.siteId = opts.siteId
        this.build(false, true)
      }

      this.$refs.salesHistoryModal.show()
    },
    clear () {
      this.siteId = null
      this.sales = []
      const startDate = new Date()
      startDate.setHours(0)
      startDate.setMinutes(0)
      startDate.setSeconds(0)
      startDate.setMilliseconds(0)
      startDate.setDate(startDate.getDate() - 7)
      const endDate = new Date()
      endDate.setHours(23)
      endDate.setMinutes(59)
      endDate.setSeconds(59)
      endDate.setMilliseconds(999)
      const maxEndDate = new Date()
      maxEndDate.setHours(23)
      maxEndDate.setMinutes(59)
      maxEndDate.setSeconds(59)
      maxEndDate.setMilliseconds(999)
      this.maxEnd = maxEndDate
      this.salesStartDate = startDate
      this.salesStartDateString = global.buildShortDateString(startDate)
      this.salesEndDate = endDate
      this.salesEndDateString = global.buildShortDateString(endDate)
      this.useSevenDays = true
      this.useFourteenDays = false
      this.useTwentyoneDays = false
    },
    formatDate (date) {
      return moment(date).format('MM/DD/YY')
    },
    onStartDateChange (text) {
      this.useSevenDays = false
      this.useFourteenDays = false
      this.useTwentyoneDays = false

      if (text.match(global.sixDigitRegex)) {
        this.salesStartDateString = `${text.substring(0, 2)}/${text.substring(
          2,
          4
        )}/${text.substring(4, 6)}`
        this.onStartDateChange(this.salesStartDateString)
      } else {
        if (text.match(global.shortDateRegex)) {
          const split = text.split('/')
          this.salesStartDate = new Date(
            +split[2] + 2000,
            +split[0] - 1,
            +split[1]
          )
        }
      }
    },
    formatSelectedDate (ctx) {
      return `${('0' + (+ctx.selectedDate.getMonth() + 1)).slice(-2)}/${(
        '0' + ctx.selectedDate.getDate()
      ).slice(-2)}/${ctx.selectedDate
        .getFullYear()
        .toString()
        .substring(2, 4)}`
    },
    onStartDateContext (ctx) {
      this.salesStartDateString = this.formatSelectedDate(ctx)
    },
    onEndDateChange (text) {
      if (text.match(global.sixDigitRegex)) {
        this.salesEndDateString = `${text.substring(0, 2)}/${text.substring(
          2,
          4
        )}/${text.substring(4, 6)}`
        this.onEndDateChange(this.salesEndDateString)
      } else {
        if (text.match(global.dateRegex)) {
          const split = text.split('/')
          this.salesEndDate = new Date(
            +split[2] + 2000,
            +split[0] - 1,
            +split[1]
          )
        }
      }
    },
    onEndDateContext (ctx) {
      this.salesEndDateString = this.formatSelectedDate(ctx)
    },
    quickCalendar (days) {
      if (days === 7) {
        this.useSevenDays = true
        this.useFourteenDays = false
        this.useTwentyoneDays = false
      } else if (days === 14) {
        this.useSevenDays = false
        this.useFourteenDays = true
        this.useTwentyoneDays = false
      } else if (days === 21) {
        this.useSevenDays = false
        this.useFourteenDays = false
        this.useTwentyoneDays = true
      }

      const startDate = new Date()
      startDate.setHours(0)
      startDate.setMinutes(0)
      startDate.setSeconds(0)
      startDate.setMilliseconds(0)
      startDate.setDate(startDate.getDate() - days)
      const endDate = new Date()
      endDate.setHours(23)
      endDate.setMinutes(59)
      endDate.setSeconds(59)
      endDate.setMilliseconds(999)
      const maxEndDate = new Date()
      maxEndDate.setHours(23)
      maxEndDate.setMinutes(59)
      maxEndDate.setSeconds(59)
      maxEndDate.setMilliseconds(999)
      this.maxEnd = maxEndDate
      this.salesStartDate = startDate
      this.salesStartDateString = global.buildShortDateString(startDate)
      this.salesEndDate = endDate
      this.salesEndDateString = global.buildShortDateString(endDate)

      this.build(true)
    },
    editItem (item) {
      item.isEditing = !item.isEditing ?? true
      item.originalValue = item.salesAmount
      this.$forceUpdate()
    },
    editItemConfirm (item) {
      SalesRepo.update(item.id, item)
      item.isEditing = false
      this.$forceUpdate()
    },
    editItemCancel (item) {
      item.isEditing = false
      item.salesAmount = item.originalValue
      this.$forceUpdate()
    },
    isToday (date) {
      const inputDate = new Date(date)
      const today = new Date()

      if (inputDate.toDateString() === today.toDateString()) {
        return 'text-success'
      } else {
        return ''
      }
    },
    salesAmountState (input) {
      var regex = /^(0|[1-9]\d*)(\.\d+)?$/
      return regex.test(input)
    }
  },
  computed: {
    endDateState () {
      if (this.salesEndDateString.match(global.shortDateRegex)) {
        if (
          this.salesEndDate.getTime() >= this.salesStartDate.getTime() &&
          this.salesEndDate.getTime() <= this.maxEnd.getTime()
        ) {
          return null
        }
      }

      return false
    },
    dataGroupedByDate () {
      return this.sales.reduce((acc, item) => {
        (acc[item.salesTimeStamp] = acc[item.salesTimeStamp] || []).push(item)
        return acc
      }, {})
    }
  }
}
</script>

<style lang="scss">
@import "~@/assets/scss/themes/theme";

.sales-history-table {
  margin-top: 20px;
}
</style>
